// DateJumpPicker.js - Kalenderknapp som öppnar en månadskalender med
// ISO-veckonummer (Spår K2.1). Används av både veckovyn (Calendar.js) och
// dagvyn (DayView.js): klick på en dag anropar onSelectDate(dag), klick på
// ett veckonummer anropar onSelectDate(veckans måndag).

import React, { useState, useEffect, useRef } from 'react';
import './DateJumpPicker.css';

const MONTHS = ['Januari', 'Februari', 'Mars', 'April', 'Maj', 'Juni', 'Juli',
  'Augusti', 'September', 'Oktober', 'November', 'December'];
const WEEKDAYS = ['Må', 'Ti', 'On', 'To', 'Fr', 'Lö', 'Sö'];

// ISO 8601-veckonummer: veckan tillhör det år där dess torsdag ligger
export const isoWeekNumber = (date) => {
  const d = new Date(Date.UTC(date.getFullYear(), date.getMonth(), date.getDate()));
  const dayNum = d.getUTCDay() || 7;
  d.setUTCDate(d.getUTCDate() + 4 - dayNum);
  const yearStart = new Date(Date.UTC(d.getUTCFullYear(), 0, 1));
  return Math.ceil(((d - yearStart) / 86400000 + 1) / 7);
};

// ISO-veckoåret (torsdagens år): mån 29 dec 2025 ligger i vecka 1 2026
export const isoWeekYear = (date) => {
  const d = new Date(date.getFullYear(), date.getMonth(), date.getDate());
  d.setDate(d.getDate() + 4 - (d.getDay() || 7));
  return d.getFullYear();
};

// Måndagen i veckan som innehåller date (lokal tid, 00:00)
export const mondayOf = (date) => {
  const d = new Date(date.getFullYear(), date.getMonth(), date.getDate());
  d.setDate(d.getDate() - ((d.getDay() + 6) % 7));
  return d;
};

const sameDay = (a, b) => a && b
  && a.getFullYear() === b.getFullYear()
  && a.getMonth() === b.getMonth()
  && a.getDate() === b.getDate();

// Veckorna (rader om 7 dagar, måndag först) som täcker månaden
const monthWeeks = (year, month) => {
  const weeks = [];
  let day = mondayOf(new Date(year, month, 1));
  while (weeks.length === 0 || day.getMonth() === month) {
    const week = [];
    for (let i = 0; i < 7; i++) {
      week.push(new Date(day));
      day.setDate(day.getDate() + 1);
    }
    weeks.push(week);
  }
  return weeks;
};

const DateJumpPicker = ({ selectedDate, onSelectDate, highlightWeek = false }) => {
  const [open, setOpen] = useState(false);
  const [viewMonth, setViewMonth] = useState(() => {
    const d = selectedDate || new Date();
    return new Date(d.getFullYear(), d.getMonth(), 1);
  });
  const rootRef = useRef(null);

  // Öppna alltid på den månad som visas i kalendern
  useEffect(() => {
    if (open) {
      const d = selectedDate || new Date();
      setViewMonth(new Date(d.getFullYear(), d.getMonth(), 1));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  // Stäng vid klick utanför eller Escape
  useEffect(() => {
    if (!open) return undefined;
    const onDown = (e) => {
      if (rootRef.current && !rootRef.current.contains(e.target)) setOpen(false);
    };
    const onKey = (e) => { if (e.key === 'Escape') setOpen(false); };
    document.addEventListener('mousedown', onDown);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', onDown);
      document.removeEventListener('keydown', onKey);
    };
  }, [open]);

  const shiftMonth = (delta) => {
    setViewMonth(m => new Date(m.getFullYear(), m.getMonth() + delta, 1));
  };

  const pick = (date) => {
    setOpen(false);
    onSelectDate(new Date(date.getFullYear(), date.getMonth(), date.getDate()));
  };

  const today = new Date();
  const selectedMonday = selectedDate ? mondayOf(selectedDate) : null;
  const weeks = monthWeeks(viewMonth.getFullYear(), viewMonth.getMonth());

  return (
    <div className="date-jump" ref={rootRef}>
      <button
        type="button"
        className={`nav-btn date-jump-btn ${open ? 'active' : ''}`}
        onClick={() => setOpen(o => !o)}
        title="Välj datum eller vecka"
        aria-label="Välj datum eller vecka"
        aria-expanded={open}
      >
        <i className="fas fa-calendar-alt"></i>
      </button>
      {open && (
        <div className="date-jump-popover" role="dialog" aria-label="Välj datum">
          <div className="date-jump-header">
            <button type="button" className="date-jump-arrow" onClick={() => shiftMonth(-1)} title="Föregående månad" aria-label="Föregående månad">
              <i className="fas fa-chevron-left"></i>
            </button>
            <span className="date-jump-month">
              {MONTHS[viewMonth.getMonth()]} {viewMonth.getFullYear()}
            </span>
            <button type="button" className="date-jump-arrow" onClick={() => shiftMonth(1)} title="Nästa månad" aria-label="Nästa månad">
              <i className="fas fa-chevron-right"></i>
            </button>
          </div>
          <table className="date-jump-grid">
            <thead>
              <tr>
                <th className="date-jump-weekcol" title="Veckonummer">v.</th>
                {WEEKDAYS.map(w => <th key={w}>{w}</th>)}
              </tr>
            </thead>
            <tbody>
              {weeks.map(week => {
                const monday = week[0];
                const isSelectedWeek = highlightWeek && sameDay(monday, selectedMonday);
                const weekNo = isoWeekNumber(monday);
                return (
                  <tr key={monday.toISOString()} className={isSelectedWeek ? 'selected-week' : ''}>
                    <td className="date-jump-weekcol">
                      <button
                        type="button"
                        className="date-jump-week"
                        onClick={() => pick(monday)}
                        title={`Gå till vecka ${weekNo}`}
                      >
                        {weekNo}
                      </button>
                    </td>
                    {week.map(day => {
                      const classes = ['date-jump-day'];
                      if (day.getMonth() !== viewMonth.getMonth()) classes.push('other-month');
                      if (sameDay(day, today)) classes.push('today');
                      if (!highlightWeek && sameDay(day, selectedDate)) classes.push('selected');
                      if (day.getDay() === 0 || day.getDay() === 6) classes.push('weekend');
                      return (
                        <td key={day.toISOString()}>
                          <button
                            type="button"
                            className={classes.join(' ')}
                            onClick={() => pick(day)}
                          >
                            {day.getDate()}
                          </button>
                        </td>
                      );
                    })}
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
};

export default DateJumpPicker;
